import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList
} from 'recharts';
import {
  ArrowLeft, Target, IndianRupee, Users, TrendingUp, Zap, AlertCircle,
  ExternalLink, RefreshCw, FileText, Download, Building2
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ─── DESIGN TOKENS ─────────────────────────────────────────
const FONT = { heading: "'Manrope', sans-serif", body: "'IBM Plex Sans', sans-serif", data: "'JetBrains Mono', monospace" };

// ─── UTILITIES ─────────────────────────────────────────────
const formatCurrency = (val) => {
  if (!val) return '₹0';
  return `₹${Number(val).toLocaleString('en-IN')}`;
};

// ─── KPI CARD ──────────────────────────────────────────────
function KpiCard({ label, value, subtitle, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-medium uppercase tracking-widest text-slate-400" style={{ fontFamily: FONT.body }}>{label}</span>
        {Icon && <div className="w-8 h-8 rounded-sm bg-slate-50 border border-slate-100 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-500" /></div>}
      </div>
      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{value}</p>
      {subtitle && <p className="text-[10px] text-slate-400 mt-1" style={{ fontFamily: FONT.body }}>{subtitle}</p>}
    </div>
  );
}

// ─── CUSTOM TOOLTIP ────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-lg p-3" style={{ fontFamily: FONT.body }}>
      <p className="text-xs font-semibold text-slate-900 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-slate-600">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-semibold" style={{ fontFamily: FONT.data }}>{formatCurrency(p.value * 100000)}</span>
        </p>
      ))}
    </div>
  );
}

// ─── MEMBER DETAIL PANEL ───────────────────────────────────
function MemberDetailPanel({ member, isOpen, onClose }) {
  if (!member) return null;
  const MetricRow = ({ label, value, highlight }) => (
    <div className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500" style={{ fontFamily: FONT.body }}>{label}</span>
      <span className={`text-sm font-semibold ${highlight || 'text-slate-900'}`} style={{ fontFamily: FONT.data }}>{value}</span>
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[440px] sm:max-w-[440px] bg-white overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-slate-900" style={{ fontFamily: FONT.heading }}>Target Plan Details</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <div className="border-b border-slate-200 pb-4">
            <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>{member.emp_name}</h3>
            <div className="flex gap-2 mt-2">
              <Badge variant="outline" className="text-[10px] rounded-sm">{member.emp_code}</Badge>
              <Badge variant="outline" className="text-[10px] rounded-sm">{member.branch}</Badge>
              <Badge variant="outline" className="text-[10px] rounded-sm">Filed: {member.date_of_filling}</Badge>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2"><Target className="w-3.5 h-3.5" /> Sales Targets</h4>
            <MetricRow label="Min Committed Sales" value={formatCurrency(member.min_committed_sales)} />
            <MetricRow label="Tentative Target" value={formatCurrency(member.tentative_target)} highlight="text-blue-600" />
            <MetricRow label="From Existing Leads" value={formatCurrency(member.from_existing_leads)} />
            <MetricRow label="Other Amounts" value={formatCurrency(member.other_amount)} />
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2"><IndianRupee className="w-3.5 h-3.5" /> Payments & Receivables</h4>
            <MetricRow label="Committed Payment" value={formatCurrency(member.total_committed_payment)} highlight="text-emerald-600" />
            <MetricRow label="Current Receivable" value={formatCurrency(member.current_receivable)} />
            <MetricRow label="Expected @50%" value={formatCurrency(member.expected_receivable_50)} />
            <MetricRow label="Overdue Amount" value={formatCurrency(member.overdue)} highlight={member.overdue > 0 ? 'text-red-500' : 'text-slate-900'} />
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-sm p-4">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Leads</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-white rounded-sm border border-slate-100">
                <p className="text-[10px] text-slate-400 uppercase">Min Leads</p>
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{member.min_leads}</p>
              </div>
              <div className="text-center p-3 bg-white rounded-sm border border-slate-100">
                <p className="text-[10px] text-slate-400 uppercase">Total Leads</p>
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{member.total_leads}</p>
              </div>
            </div>
          </div>

          {(member.feedback || member.concerns) && (
            <div className="border-t border-slate-200 pt-4 space-y-3">
              {member.feedback && <div><p className="text-[10px] uppercase text-slate-400 mb-1">Feedback</p><p className="text-xs text-slate-700">{member.feedback}</p></div>}
              {member.concerns && member.concerns !== 'None' && <div><p className="text-[10px] uppercase text-slate-400 mb-1">Concerns</p><p className="text-xs text-amber-600">{member.concerns}</p></div>}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <p className="text-[10px] text-slate-400">Email: <span className="text-slate-600">{member.emp_email}</span></p>
          </div>
          {member.report_link && member.report_link !== '' && (
            <a href={member.report_link} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline mt-2">
              <ExternalLink className="w-3.5 h-3.5" />Lead Distribution Report
            </a>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── ERROR BOUNDARY ────────────────────────────────────────
class DashboardErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center p-8"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" /><h2 className="text-lg font-bold text-slate-900 mb-2">Dashboard Error</h2><Button onClick={() => window.location.reload()} className="rounded-sm">Reload</Button></div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── MAIN DASHBOARD ────────────────────────────────────────
function TargetPlanDashboardInner() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const dashRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMonth) params.plan_month = selectedMonth;
      const res = await api.get(`/target-plan-dashboard/${formId}/overview`, { params });
      setData(res.data);
      if (!selectedMonth && res.data.available_months?.length > 0) {
        setSelectedMonth(res.data.available_months[0]);
      }
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  }, [formId, selectedMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const exportPDF = async () => {
    if (!dashRef.current) return;
    try {
      const canvas = await html2canvas(dashRef.current, { scale: 2, backgroundColor: '#ffffff' });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pW = pdf.internal.pageSize.getWidth(), pH = pdf.internal.pageSize.getHeight();
      const r = Math.min(pW / canvas.width, pH / canvas.height);
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', (pW - canvas.width * r) / 2, 0, canvas.width * r, canvas.height * r);
      pdf.save(`target-plan-${selectedMonth || 'report'}.pdf`);
    } catch (err) { console.error('PDF error:', err); }
  };

  const summary = data?.team_summary || {};
  const members = data?.team_members || [];
  const branches = data?.branch_summary || [];

  // Chart data: Tentative target per member
  const chartData = members.map(m => ({
    name: m.emp_name.split(' ')[0],
    target: m.tentative_target / 100000,
    committed: m.min_committed_sales / 100000,
    payment: m.total_committed_payment / 100000,
  }));

  if (loading && !data) {
    return (
      <div className="flex h-screen bg-white">
        <aside className="w-56 bg-slate-900 flex-shrink-0" />
        <div className="flex-1 flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-400 mx-auto" /><p className="text-slate-400 text-sm mt-3">Loading dashboard...</p></div></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50/50 overflow-hidden" style={{ fontFamily: FONT.body }}>
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 flex flex-col flex-shrink-0">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-7 h-7 rounded-sm bg-white/10 flex items-center justify-center"><Target className="w-4 h-4 text-white" /></div>
            <p className="text-white text-sm font-bold" style={{ fontFamily: FONT.heading }}>Target Plan</p>
          </div>
          <p className="text-slate-500 text-[10px] pl-9 leading-tight">Sales Monthly Target Plan</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 mt-2">
          <div className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs font-medium bg-white text-slate-900 shadow-sm">
            <Users className="w-3.5 h-3.5 flex-shrink-0" />Team Overview
          </div>
        </nav>
        {/* Sidebar stats */}
        <div className="px-4 pb-3 space-y-2">
          <div className="bg-slate-800/50 rounded-sm p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Tentative Target</p>
            <p className="text-sm font-bold text-white" style={{ fontFamily: FONT.data }}>{formatCurrency(summary.tentative_target)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-sm p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Committed Payment</p>
            <p className="text-sm font-bold text-emerald-400" style={{ fontFamily: FONT.data }}>{formatCurrency(summary.total_committed_payment)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-sm p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Overdue</p>
            <p className={`text-sm font-bold ${summary.overdue > 0 ? 'text-red-400' : 'text-slate-400'}`} style={{ fontFamily: FONT.data }}>{formatCurrency(summary.overdue)}</p>
          </div>
        </div>
        <div className="p-3 border-t border-slate-800">
          <button onClick={() => navigate('/dashboard')} data-testid="back-to-main" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs text-slate-500 hover:bg-slate-800 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />Back to Command Center
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4 flex-shrink-0">
          <h1 className="text-sm font-bold text-slate-900 flex-1" style={{ fontFamily: FONT.heading }}>{data?.form?.name || 'Sales Monthly Target Plan'}</h1>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Plan Month</span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px] h-8 rounded-sm bg-white border-slate-200 text-slate-700 text-xs" data-testid="month-selector"><SelectValue placeholder="Select month" /></SelectTrigger>
              <SelectContent>{(data?.available_months || []).map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={exportPDF} className="h-8 rounded-sm border-slate-200 text-slate-600 text-xs" data-testid="export-pdf"><Download className="w-3.5 h-3.5 mr-1.5" />Export PDF</Button>
            <button onClick={loadData} className="h-8 w-8 flex items-center justify-center rounded-sm bg-white border border-slate-200 text-slate-400 hover:text-slate-600" data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div ref={dashRef} className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-cards">
          <KpiCard label="Team Members" value={summary.member_count || 0} icon={Users} />
          <KpiCard label="Tentative Target" value={formatCurrency(summary.tentative_target)} icon={Target} subtitle="Total team target" />
          <KpiCard label="Committed Payment" value={formatCurrency(summary.total_committed_payment)} icon={IndianRupee} subtitle="Expected to receive" />
          <KpiCard label="Total Leads" value={summary.total_leads || 0} icon={Zap} subtitle={`Min: ${summary.min_leads || 0}`} />
          <KpiCard label="Overdue Amount" value={formatCurrency(summary.overdue)} icon={AlertCircle} subtitle="Pending collection" />
        </div>

        {/* Financials Summary Bar */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500" style={{ fontFamily: FONT.heading }}>Financial Planning Summary</h3>
          </div>
          <div className="p-5 grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
            {[
              { label: 'Min Committed Sales', value: formatCurrency(summary.min_committed_sales) },
              { label: 'Tentative Target', value: formatCurrency(summary.tentative_target) },
              { label: 'From Existing Leads', value: formatCurrency(summary.from_existing_leads) },
              { label: 'Current Receivable', value: formatCurrency(summary.current_receivable) },
              { label: 'Expected @50%', value: formatCurrency(summary.expected_receivable_50) },
              { label: 'Overdue', value: formatCurrency(summary.overdue), color: summary.overdue > 0 ? 'text-red-500' : undefined },
            ].map((m, i) => (
              <div key={i}>
                <p className="text-[9px] uppercase text-slate-400 mb-1.5 tracking-wider" style={{ fontFamily: FONT.body }}>{m.label}</p>
                <p className={`text-lg font-bold ${m.color || 'text-slate-900'}`} style={{ fontFamily: FONT.data }}>{m.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Chart: Target by Member */}
        {chartData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>Target Plan by Team Member</h3>
                <p className="text-[10px] text-slate-400 mt-0.5">Amounts in Lakhs (₹L) | Tentative Target vs Min Committed vs Committed Payment</p>
              </div>
              <div className="flex items-center gap-4 text-[10px]">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-900" />Tentative Target</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-300" />Min Committed</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" />Committed Payment</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }} barGap={3} barSize={24}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#334155', fontFamily: FONT.body, fontWeight: 600 }} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b', fontFamily: FONT.data }} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={v => `₹${v}L`} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="target" name="Tentative Target" fill="#0f172a" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="target" position="top" formatter={v => `₹${v.toFixed(1)}L`} style={{ fontSize: 9, fill: '#0f172a', fontWeight: 600, fontFamily: FONT.data }} />
                </Bar>
                <Bar dataKey="committed" name="Min Committed" fill="#cbd5e1" radius={[2, 2, 0, 0]} />
                <Bar dataKey="payment" name="Committed Payment" fill="#10b981" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Branch Summary */}
        {branches.length > 1 && (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: FONT.heading }}><Building2 className="w-4 h-4 text-slate-500" />Branch-wise Summary</h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-y divide-slate-100">
              {branches.map(b => (
                <div key={b.branch} className="p-4 text-center">
                  <p className="text-xs font-semibold text-slate-900 mb-1" style={{ fontFamily: FONT.heading }}>{b.branch}</p>
                  <p className="text-[10px] text-slate-400 mb-2">{b.members} member{b.members > 1 ? 's' : ''}</p>
                  <p className="text-lg font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{formatCurrency(b.tentative_target)}</p>
                  <p className="text-[10px] text-slate-400">Target</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Members Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>Individual Target Plans</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Click on a team member to view full details</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-slate-200 hover:bg-slate-50">
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Employee</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Branch</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Min Sales</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Tentative Target</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">From Leads</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Committed Payment</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Overdue</TableHead>
                <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-center">Leads</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map(m => (
                <TableRow key={m.emp_code} className="border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => { setSelectedMember(m); setPanelOpen(true); }} data-testid={`member-row-${m.emp_code}`}>
                  <TableCell className="py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: FONT.body }}>{m.emp_name}</p>
                      <p className="text-[10px] text-slate-400" style={{ fontFamily: FONT.data }}>{m.emp_code}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] rounded-sm">{m.branch}</Badge></TableCell>
                  <TableCell className="text-right text-xs text-slate-500" style={{ fontFamily: FONT.data }}>{formatCurrency(m.min_committed_sales)}</TableCell>
                  <TableCell className="text-right text-xs font-semibold text-slate-900" style={{ fontFamily: FONT.data }}>{formatCurrency(m.tentative_target)}</TableCell>
                  <TableCell className="text-right text-xs text-slate-600" style={{ fontFamily: FONT.data }}>{formatCurrency(m.from_existing_leads)}</TableCell>
                  <TableCell className="text-right text-xs font-semibold text-emerald-600" style={{ fontFamily: FONT.data }}>{formatCurrency(m.total_committed_payment)}</TableCell>
                  <TableCell className={`text-right text-xs font-semibold ${m.overdue > 0 ? 'text-red-500' : 'text-slate-400'}`} style={{ fontFamily: FONT.data }}>{m.overdue > 0 ? formatCurrency(m.overdue) : '--'}</TableCell>
                  <TableCell className="text-center text-xs text-slate-700" style={{ fontFamily: FONT.data }}>{m.min_leads} / {m.total_leads}</TableCell>
                </TableRow>
              ))}
              {members.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-12"><AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p>No target plans for this month</p></TableCell></TableRow>
              )}
              {members.length > 0 && (
                <TableRow className="bg-slate-100/80 border-t-2 border-slate-300 font-semibold hover:bg-slate-100/80">
                  <TableCell className="py-3 text-xs text-slate-700" style={{ fontFamily: FONT.body }}>Total ({members.length})</TableCell>
                  <TableCell />
                  <TableCell className="text-right text-xs text-slate-700" style={{ fontFamily: FONT.data }}>{formatCurrency(members.reduce((s, m) => s + (m.min_committed_sales || 0), 0))}</TableCell>
                  <TableCell className="text-right text-xs font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{formatCurrency(members.reduce((s, m) => s + (m.tentative_target || 0), 0))}</TableCell>
                  <TableCell className="text-right text-xs text-slate-700" style={{ fontFamily: FONT.data }}>{formatCurrency(members.reduce((s, m) => s + (m.from_existing_leads || 0), 0))}</TableCell>
                  <TableCell className="text-right text-xs font-bold text-emerald-600" style={{ fontFamily: FONT.data }}>{formatCurrency(members.reduce((s, m) => s + (m.total_committed_payment || 0), 0))}</TableCell>
                  <TableCell className="text-right text-xs font-bold text-red-500" style={{ fontFamily: FONT.data }}>{formatCurrency(members.reduce((s, m) => s + (m.overdue || 0), 0))}</TableCell>
                  <TableCell className="text-center text-xs text-slate-700" style={{ fontFamily: FONT.data }}>{members.reduce((s, m) => s + (m.min_leads || 0), 0)} / {members.reduce((s, m) => s + (m.total_leads || 0), 0)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Detail Panel */}
      <MemberDetailPanel member={selectedMember} isOpen={panelOpen} onClose={() => setPanelOpen(false)} />
      </div>
    </div>
  );
}

export default function TargetPlanDashboard() {
  return <DashboardErrorBoundary><TargetPlanDashboardInner /></DashboardErrorBoundary>;
}
